import type {
  Lane,
  Card,
  KpiSnapshot,
  ChecklistItem,
  FlowNode,
  FlowEdge,
  BoardState,
  FilterState,
  LaneId,
  Owner,
} from "./types";
import { parseCSVToCards } from "../lib/csvTasks";

// ── Embedded CSV (master source of truth) ───────────────────────
// This file is the canonical dataset. To update tasks:
// 1. Edit DOCUMENTATION/gtm/official/csv_v2/cards_master_v2.csv
// 2. Copy it to src/data/cards_master_v2.csv
// 3. Run `bun run sync:cards-csv-module`
// 4. Verify with `bun test`

import { cardsMasterV2Csv as csvText } from "./cards_master_v2.generated";

const csvResult = parseCSVToCards(csvText);
if (csvResult.errors.length > 0) {
  console.error("CSV parse errors in cards_master_v2.csv:", csvResult.errors);
}

const allCards: Card[] = csvResult.cards;

// ── KPI Snapshots (pre-launch zeros — updated weekly via Convex) ──

export const kpiSnapshots: KpiSnapshot[] = [
  { weekStart: "2026-03-02", weekEnd: "2026-03-08", leadsNew: 0, demosBooked: 0, demosCompleted: 0, trialsStarted: 0, closedWon: 0, closedLost: 0, newMrrEur: 0, trialToPaidPct: 0, avgTimeToValueHours: 0, winRatePct: 0, topChannel: "", topObjection: "", activityCompletionPct: 0, followupCoveragePct: 0 },
  { weekStart: "2026-03-09", weekEnd: "2026-03-15", leadsNew: 0, demosBooked: 0, demosCompleted: 0, trialsStarted: 0, closedWon: 0, closedLost: 0, newMrrEur: 0, trialToPaidPct: 0, avgTimeToValueHours: 0, winRatePct: 0, topChannel: "", topObjection: "", activityCompletionPct: 0, followupCoveragePct: 0 },
  { weekStart: "2026-03-16", weekEnd: "2026-03-22", leadsNew: 0, demosBooked: 0, demosCompleted: 0, trialsStarted: 0, closedWon: 0, closedLost: 0, newMrrEur: 0, trialToPaidPct: 0, avgTimeToValueHours: 0, winRatePct: 0, topChannel: "", topObjection: "", activityCompletionPct: 0, followupCoveragePct: 0 },
  { weekStart: "2026-03-23", weekEnd: "2026-03-29", leadsNew: 0, demosBooked: 0, demosCompleted: 0, trialsStarted: 0, closedWon: 0, closedLost: 0, newMrrEur: 0, trialToPaidPct: 0, avgTimeToValueHours: 0, winRatePct: 0, topChannel: "", topObjection: "", activityCompletionPct: 0, followupCoveragePct: 0 },
  { weekStart: "2026-03-30", weekEnd: "2026-04-05", leadsNew: 0, demosBooked: 0, demosCompleted: 0, trialsStarted: 0, closedWon: 0, closedLost: 0, newMrrEur: 0, trialToPaidPct: 0, avgTimeToValueHours: 0, winRatePct: 0, topChannel: "", topObjection: "", activityCompletionPct: 0, followupCoveragePct: 0 },
];

// ── Lane Definitions ────────────────────────────────────────────

export const lanes: Lane[] = [
  {
    id: "objective_kpis",
    title: "Objectives & KPIs",
    description: "Launch week + month 1 targets",
    color: "#266DF0",
    cards: allCards.filter((c) => c.laneId === "objective_kpis"),
  },
  {
    id: "offer_pricing",
    title: "Offer & Pricing",
    description: "Founder / Startup / Scale-Up pricing",
    color: "#8B5CF6",
    cards: allCards.filter((c) => c.laneId === "offer_pricing"),
  },
  {
    id: "channels",
    title: "Channels",
    description: "Distribution ops + Reddit + directories",
    color: "#06B6D4",
    cards: allCards.filter((c) => c.laneId === "channels"),
  },
  {
    id: "commercial_pipeline",
    title: "Commercial Pipeline",
    description: "Sequences, batches, deals and revenue ops",
    color: "#F59E0B",
    cards: allCards.filter((c) => c.laneId === "commercial_pipeline"),
  },
  {
    id: "weekly_content",
    title: "Weekly Content System",
    description: "Founder content, SEO, proof assets",
    color: "#10B981",
    cards: allCards.filter((c) => c.laneId === "weekly_content"),
  },
  {
    id: "revops_tracking",
    title: "RevOps & Tracking",
    description: "Pipeline discipline, attribution, scorecards",
    color: "#EF4444",
    cards: allCards.filter((c) => c.laneId === "revops_tracking"),
  },
  {
    id: "feedback_loop",
    title: "Product ↔ Sales Feedback",
    description: "Daily syncs, retrospectives, iteration loops",
    color: "#EC4899",
    cards: allCards.filter((c) => c.laneId === "feedback_loop"),
  },
];

// ── Flow Path ───────────────────────────────────────────────────

export const flowNodes: FlowNode[] = [
  { id: "flow-traffic", label: "Traffic / Outreach", laneId: "channels" },
  { id: "flow-lead", label: "Lead Capture", laneId: "commercial_pipeline" },
  { id: "flow-demo", label: "Demo", laneId: "commercial_pipeline" },
  { id: "flow-trial", label: "Trial", laneId: "commercial_pipeline" },
  { id: "flow-close", label: "Annual Close", laneId: "commercial_pipeline" },
  { id: "flow-onboard", label: "Onboarding", laneId: "feedback_loop" },
  { id: "flow-testimonial", label: "Testimonial / Referral", laneId: "weekly_content" },
  { id: "flow-proof", label: "Proof Content", laneId: "weekly_content" },
];

export const flowEdges: FlowEdge[] = [
  { from: "flow-traffic", to: "flow-lead" },
  { from: "flow-lead", to: "flow-demo" },
  { from: "flow-demo", to: "flow-trial" },
  { from: "flow-trial", to: "flow-close" },
  { from: "flow-close", to: "flow-onboard" },
  { from: "flow-onboard", to: "flow-testimonial" },
  { from: "flow-testimonial", to: "flow-proof" },
  { from: "flow-proof", to: "flow-traffic" },
];

// ── Daily Checklist (derived from recurring_daily cards) ────────

function deriveChecklistSection(owner: Owner): "joel" | "albert" | "joint" {
  if (owner === "Joel") return "joel";
  if (owner === "Albert") return "albert";
  return "joint";
}

export const dailyChecklist: ChecklistItem[] = allCards
  .filter((c) => c.tags.includes("task:recurring_daily"))
  .map((c) => ({
    id: c.id,
    owner: c.owner,
    text: c.title,
    checked: false,
    section: deriveChecklistSection(c.owner),
  }));

// ── Default Filter State ────────────────────────────────────────

export const defaultFilters: FilterState = {
  owner: "all",
  status: "all",
  channel: "all",
  week: "all",
  search: "",
};

// ── Full Initial Board State ────────────────────────────────────

export function createInitialBoardState(): BoardState {
  return {
    lanes,
    kpis: kpiSnapshots,
    checklist: dailyChecklist,
    filters: { ...defaultFilters },
    selectedCardId: null,
    flowNodes,
    flowEdges,
  };
}
